import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import React, { useState, useRef } from 'react';
import cn from 'classnames';
import _ from 'lodash';

import { selectReactionsMessages } from 'client/redux/pubnub/selectors';
import { selectRoomId } from 'client/redux/room/selectors';
import {
  useAppSelector,
  useReactionsCount,
  useOnClickOutside,
  useTranslation,
  useAppDispatch,
  useResponsive,
} from 'client/hooks';
import { thousandSeparator } from 'utils/thousand-separator';
import { PubnubMsgsItem } from 'client/redux/pubnub/types';
import { formatNumber } from 'client/utils/format-number';
import { EMOJI_TYPES } from 'client/types';
import { USER_ROLES } from 'constants/index';
import { PB_CHANNELS } from 'client/redux/pubnub/constants';
import { vwMSizes, vwDSizes } from 'utils/vwSizes';
import { getReactionsMessages } from 'client/redux/pubnub/actions';

import GeneralStatisticsList from '../../GeneralStatisticsList';
import TransitionComponent from '../../TransitionComponent';
import ListenerIcon from '../../RoomContent/ListenerIcon';
import { DrawerCloseBtn } from '../DrawerCloseBtn';
import Button from '../../Button';
import Icon from '../../Icon';

import css from './ReactionsDetailsDrawer.module.scss';

interface Props {
  visible: boolean;
  className?: string;
  userRole: USER_ROLES;
  handleClose: () => void;
  totalListenersCount?: number;
  pendingRequestsCount?: number;
  connectedListenersCount?: number;
}

const ReactionsDetailsDrawer: React.FC<Props> = (props) => {
  const {
    className = '',
    visible,
    handleClose,
    connectedListenersCount,
    pendingRequestsCount,
    totalListenersCount,
    userRole,
  } = props;
  const { translate } = useTranslation('actionsPanel');
  const { reactionsCount, total: totalReactions } = useReactionsCount();
  const reactionsMessages = useAppSelector(selectReactionsMessages);
  const [activeTab, setActiveTab] = useState<EMOJI_TYPES | 'none'>('none');
  const drawerRef = React.useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const roomId = useAppSelector(selectRoomId);
  const [isMobile] = useResponsive('MOBILE');
  const [isShowStatistic, setIsShowStatistic] = useState(false);
  const [isShowMoreBtn, setIsShowMoreBtn] = useState<boolean>(false);
  const listRef = useRef<List>(null);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore-next-line
  const cs = css[`${className}`];

  useOnClickOutside<HTMLDivElement>(drawerRef, handleClose);

  const TABS = React.useMemo(
    () => [
      {
        emoji: 'none',
      },
      {
        emoji: EMOJI_TYPES.LIKE,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.LIKE] || 0),
        count: reactionsCount?.[EMOJI_TYPES.LIKE] || 0,
        icon: EMOJI_TYPES.LIKE,
      },
      {
        emoji: EMOJI_TYPES.HEART,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.HEART] || 0),
        count: reactionsCount?.[EMOJI_TYPES.HEART] || 0,
        icon: EMOJI_TYPES.HEART,
      },
      {
        emoji: EMOJI_TYPES.SMILE,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.SMILE] || 0),
        count: reactionsCount?.[EMOJI_TYPES.SMILE] || 0,
        icon: EMOJI_TYPES.SMILE,
      },
      {
        emoji: EMOJI_TYPES.WONDER,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.WONDER] || 0),
        count: reactionsCount?.[EMOJI_TYPES.WONDER] || 0,
        icon: EMOJI_TYPES.WONDER,
      },
      {
        emoji: EMOJI_TYPES.ANGRY,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.ANGRY] || 0),
        count: reactionsCount?.[EMOJI_TYPES.ANGRY] || 0,
        icon: EMOJI_TYPES.ANGRY,
      },
      {
        emoji: EMOJI_TYPES.CRY,
        name: formatNumber(reactionsCount?.[EMOJI_TYPES.CRY] || 0),
        count: reactionsCount?.[EMOJI_TYPES.CRY] || 0,
        icon: EMOJI_TYPES.CRY,
      },
    ],
    [reactionsCount],
  );

  const currentListOfReactionMessages = React.useMemo(() => {
    if (activeTab === 'none') {
      const result = _.reduce(
        reactionsMessages,
        (acc: Array<PubnubMsgsItem>, item) => {
          acc = [...acc, ...item];
          return acc;
        },
        [],
      );
      return _.orderBy(result, 'timetoken', 'desc');
    }

    return reactionsMessages[activeTab];
  }, [activeTab, reactionsMessages]);

  const clickOnStatisticBtn = () => {
    setIsShowStatistic(!isShowStatistic);
  };

  const onTabChange = (e: React.SyntheticEvent<HTMLDivElement>) => {
    const { emoji } = e.currentTarget.dataset;
    if (emoji) setActiveTab(emoji as EMOJI_TYPES);
    if (isShowMoreBtn) {
      setIsShowMoreBtn(false);
    }
    listRef.current?.scrollTo(0);
  };

  const clickOnLoadMore = () => {
    setIsShowMoreBtn(false);
    if (activeTab !== 'none') {
      dispatch(
        getReactionsMessages({
          roomId: `${roomId}.${PB_CHANNELS.REACTIONS}.${activeTab}`,
          isLoadMore: true,
        }),
      );
    } else {
      dispatch(
        getReactionsMessages({
          roomId: [
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.HEART}`,
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.ANGRY}`,
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.SMILE}`,
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.WONDER}`,
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.CRY}`,
            `${roomId}.${PB_CHANNELS.REACTIONS}.${EMOJI_TYPES.LIKE}`,
          ],
          isLoadMore: true,
        }),
      );
    }
  };

  const showMoreBtnState = (st: boolean) => {
    setIsShowMoreBtn(st);
  };

  const listHeight = (listLength: number, size: number) => {
    return listLength * size + size / 2;
  };

  const renderList = () => {
    const isNeedShowMoreBtn =
      (reactionsCount && activeTab !== 'none' && currentListOfReactionMessages.length < reactionsCount[activeTab]) ||
      (activeTab === 'none' && currentListOfReactionMessages.length < totalReactions);
    return (
      <>
        <AutoSizer>
          {({ width: w, height: h }: { width: number; height: number }) => {
            const size = isMobile ? vwMSizes(71) : vwDSizes(71);
            const width = w || 0;
            const height = h || 0;

            return (
              <>
                <List
                  ref={listRef}
                  height={height}
                  itemCount={currentListOfReactionMessages.length}
                  itemSize={size}
                  width={width}
                  direction="rtl"
                  className={css.list}
                  onScroll={(scroll) => {
                    if (scroll.scrollOffset + height === listHeight(currentListOfReactionMessages.length, size)) {
                      showMoreBtnState(true);
                    } else {
                      showMoreBtnState(false);
                    }
                  }}
                >
                  {({ index, style }) => {
                    if (currentListOfReactionMessages[index]) {
                      const totalInfo = currentListOfReactionMessages[index];
                      const name = JSON.parse(totalInfo.message).name;
                      const iconType = totalInfo.channel.split('.')?.pop() || EMOJI_TYPES.LIKE;
                      return (
                        <div key={totalInfo.uuid} className={css.listItem} style={style}>
                          <ListenerIcon name={name} className={css.listenerAvatar} classNameOfName={css.listenerName} />

                          <Icon
                            type={activeTab === 'none' ? (iconType as EMOJI_TYPES) : activeTab}
                            className={css.reactionIcon}
                          />
                        </div>
                      );
                    }
                    return null;
                  }}
                </List>
              </>
            );
          }}
        </AutoSizer>
        {isNeedShowMoreBtn && isShowMoreBtn && (
          <Button label={translate('loadMoreButton')} className={css.loadMoreButton} onClickHandler={clickOnLoadMore} />
        )}
      </>
    );
  };

  return (
    <TransitionComponent visible={visible} type="transformRight">
      <div className={cn(css.drawer, cs)} ref={drawerRef}>
        <div className={css.line} onClick={handleClose} />
        <DrawerCloseBtn onClick={handleClose} />

        <div className={css.titleWrapper}>
          <div className={css.title}>
            {translate('reactionsDetailsDrawer.drawerTitle')} ({thousandSeparator(totalReactions)})
          </div>
          <div className={cn(css.statisticBtn, isShowStatistic && css.active)} onClick={clickOnStatisticBtn}>
            {translate('usersStatisticsDrawer.statisticBtn')}
            <i></i>
          </div>
        </div>

        <TransitionComponent visible={isShowStatistic} type="transformBottom">
          <div className={cn(css.statistic)}>
            <div className={css.statisticWrapper}>
              <GeneralStatisticsList
                totalListenersCount={totalListenersCount}
                connectedListenersCount={connectedListenersCount}
                pendingRequestsCount={pendingRequestsCount}
                userRole={userRole}
              />
            </div>
          </div>
        </TransitionComponent>

        <div className={css.tabs}>
          <div className={css.tabsI}>
            {_.map(
              TABS,
              (t, index) =>
                t.count !== 0 && (
                  <div
                    key={t.emoji}
                    className={cn(css.tab, t.emoji == activeTab && css.active)}
                    data-emoji={t.emoji}
                    onClick={onTabChange}
                  >
                    {t.icon && <Icon type={t.icon} className={css.icon} />}
                    {index === 0 ? translate('reactionsDetailsDrawer.firstTabName') : t.name}
                  </div>
                ),
            )}
          </div>
          <div className={css.content}>{renderList()}</div>
        </div>
      </div>
    </TransitionComponent>
  );
};

export default ReactionsDetailsDrawer;
